import { motion } from "framer-motion";
import { ArrowUpRight, Activity, Clock, Facebook, Mail, Target, Waves } from "lucide-react";
import CopyPill from "@/components/CopyPill";

const ICONS = { facebook: Facebook, target: Target, mail: Mail, waves: Waves, activity: Activity };

export default function ChannelCard({ channel, index }) {
  const Icon = ICONS[channel.icon];

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, delay: Math.min(index * 0.06, 0.2) }}
      id={`channel-${channel.id}`}
      data-testid={`channel-card-${channel.id}`}
      className="scroll-mt-10 rounded-2xl bg-white p-7 shadow-[0_4px_24px_-4px_rgba(7,0,93,0.06)] ring-1 ring-navy/[0.06] transition-shadow duration-300 hover:shadow-[0_16px_40px_-8px_rgba(7,0,93,0.12)] sm:p-8"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <span
            className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl"
            style={{ backgroundColor: channel.accent.bg, color: channel.accent.color }}
          >
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <h3 className="text-xl font-bold tracking-[-0.02em] text-navy">{channel.name}</h3>
            <p className="mt-1 text-sm text-navy/60">{channel.sub}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-mist px-3 py-1 text-xs font-semibold text-navy/50">
            <Clock className="h-3 w-3" />
            {channel.est}
          </span>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2.5">
        {channel.pills.map((pill) => (
          <CopyPill key={pill.value + pill.testId} value={pill.value} label={pill.label} testId={pill.testId} />
        ))}
      </div>

      {channel.note && (
        <p className="mt-5 rounded-xl border border-mist bg-fog px-4 py-3 text-[13px] font-medium leading-relaxed text-navy/70">
          {channel.note}
        </p>
      )}

      <ol className="mt-7 space-y-4">
        {channel.steps.map((step, i) => (
          <li key={i} className="flex gap-4 text-[15px] leading-relaxed text-navy/80">
            <span className="grid h-7 w-7 shrink-0 items-center justify-center rounded-full border border-mist text-xs font-bold text-navy">
              {i + 1}
            </span>
            <span className="pt-0.5">{step}</span>
          </li>
        ))}
      </ol>

      <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-mist pt-5">
        {(channel.links || [channel.link]).map((l) => (
          <a
            key={l.label}
            data-testid={l.testId || `channel-open-${channel.id}`}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 text-sm font-bold text-link transition-all hover:gap-3"
          >
            {l.label}
            <ArrowUpRight className="h-4 w-4" />
          </a>
        ))}
      </div>
    </motion.article>
  );
}
